import { http } from "./http";
import type { AccessLevel } from "./manufacturers";
import type { Shipper, TrackingOrder } from "./orders";

export type AdminUserSummary = {
    id: string;
    userName: string;
    administrator: boolean;
    disabled: boolean;
    lockedOut: boolean;
    lockoutEnd: string | null;
    manufacturerCount: number;
    accountCount: number;
};

export type AdminUser = {
    id: string;
    userName: string;
    administrator: boolean;
    lastEnabledAdministrator: boolean;
    disabled: boolean;
    lockedOut: boolean;
    lockoutEnd: string | null;
    accessFailedCount: number;
    manufacturers: { manufacturerId: string; name: string; level: AccessLevel }[];
    accounts: { accountId: string; name: string; domain: string; level: AccessLevel }[];
};

export type CreatedUser = { id: string; userName: string; temporaryPassword: string };

export type AdminManufacturerSummary = {
    id: string;
    name: string;
    slug: string;
    userCount: number;
    apiKeyCount: number;
    orderCount: number;
};

export type AdminManufacturer = {
    id: string;
    name: string;
    slug: string;
    hasLogo: boolean;
    logoUrl: string;
    hasFavicon: boolean;
    faviconUrl: string;
    customFooter: string | null;
    orderCount: number;
    inventoryCount: number;
    users: { userId: string; userName: string; level: AccessLevel; disabled: boolean; lockedOut: boolean }[];
    apiKeys: { id: string; name: string; keyPrefix: string; createdAt: string | null }[];
    shippers: { id: string; name: string; shipperId: string | null; shipperName: string | null }[];
};

export type AdminAccountSummary = {
    id: string;
    name: string;
    domain: string;
    userCount: number;
    orderCount: number;
    firstOrderFrom: string | null;
};

export type AdminAccount = {
    id: string;
    name: string;
    domain: string;
    orderCount: number;
    firstOrderFrom: string | null;
    users: { userId: string; userName: string; level: AccessLevel; disabled: boolean; lockedOut: boolean }[];
};

export type Carrier = { id: string; name: string; trackingOrderUrl: string; usedBy: number };

export type AdminOrderSummary = {
    id: string;
    purchaseOrderNumber: string;
    salesOrderNumber: string | null;
    accountId: string | null;
    accountName: string | null;
    manufacturerId: string;
    manufacturerName: string;
    lineCount: number;
    orderDate: string | null;
    estimatedShipDate: string | null;
    updatedAt: string;
    shipments: Shipper[];
};

export type AdminOrderQuery = { search?: string | undefined; accountIds?: string[] | undefined; manufacturerId?: string | undefined };

export const adminApi = {
    orders: {
        async list(query: AdminOrderQuery): Promise<AdminOrderSummary[]> {
            const { data } = await http.get<AdminOrderSummary[]>("/api/v0/admin/orders", { params: query });
            return data;
        },
        async get(id: string): Promise<TrackingOrder> {
            const { data } = await http.get<TrackingOrder>(`/api/v0/admin/orders/${id}`);
            return data;
        },
    },
    users: {
        async list(search = ""): Promise<AdminUserSummary[]> {
            const { data } = await http.get<AdminUserSummary[]>("/api/v0/admin/users", { params: search ? { search } : {} });
            return data;
        },
        async get(id: string): Promise<AdminUser> {
            const { data } = await http.get<AdminUser>(`/api/v0/admin/users/${id}`);
            return data;
        },
        async create(email: string, administrator: boolean): Promise<CreatedUser> {
            const { data } = await http.post<CreatedUser>("/api/v0/admin/users", { email, administrator });
            return data;
        },
        async resetPassword(id: string): Promise<string> {
            const { data } = await http.post<{ temporaryPassword: string }>(`/api/v0/admin/users/${id}/reset-password`);
            return data.temporaryPassword;
        },
        async disable(id: string): Promise<void> {
            await http.post(`/api/v0/admin/users/${id}/disable`);
        },
        async impersonate(id: string): Promise<void> {
            await http.post(`/api/v0/admin/users/${id}/impersonate`);
        },
        async enable(id: string): Promise<void> {
            await http.post(`/api/v0/admin/users/${id}/enable`);
        },
        async setAdministrator(id: string, administrator: boolean): Promise<void> {
            await http.put(`/api/v0/admin/users/${id}/administrator`, { administrator });
        },
        async setManufacturerAccess(id: string, manufacturerId: string, level: AccessLevel): Promise<void> {
            await http.put(`/api/v0/admin/users/${id}/manufacturers/${manufacturerId}`, { level });
        },
        async removeManufacturerAccess(id: string, manufacturerId: string): Promise<void> {
            await http.delete(`/api/v0/admin/users/${id}/manufacturers/${manufacturerId}`);
        },
        async setAccountAccess(id: string, accountId: string, level: AccessLevel): Promise<void> {
            await http.put(`/api/v0/admin/users/${id}/accounts/${accountId}`, { level });
        },
        async removeAccountAccess(id: string, accountId: string): Promise<void> {
            await http.delete(`/api/v0/admin/users/${id}/accounts/${accountId}`);
        },
    },
    manufacturers: {
        async list(search = ""): Promise<AdminManufacturerSummary[]> {
            const { data } = await http.get<AdminManufacturerSummary[]>("/api/v0/admin/manufacturers", { params: search ? { search } : {} });
            return data;
        },
        async create(name: string, slug: string): Promise<{ id: string }> {
            const { data } = await http.post<{ id: string }>("/api/v0/admin/manufacturers", { name, slug });
            return data;
        },
        async get(id: string): Promise<AdminManufacturer> {
            const { data } = await http.get<AdminManufacturer>(`/api/v0/admin/manufacturers/${id}`);
            return data;
        },
        async setUserAccess(id: string, userId: string, level: AccessLevel): Promise<void> {
            await http.put(`/api/v0/admin/manufacturers/${id}/users/${userId}`, { level });
        },
        async removeUserAccess(id: string, userId: string): Promise<void> {
            await http.delete(`/api/v0/admin/manufacturers/${id}/users/${userId}`);
        },
        async createApiKey(id: string, name: string): Promise<string> {
            const { data } = await http.post<{ apiKey: string }>(`/api/v0/admin/manufacturers/${id}/apikeys`, { name });
            return data.apiKey;
        },
        async revokeApiKey(id: string, apiKeyId: string): Promise<void> {
            await http.delete(`/api/v0/admin/manufacturers/${id}/apikeys/${apiKeyId}`);
        },
        async addShipper(id: string, name: string, shipperId: string | null): Promise<void> {
            await http.post(`/api/v0/admin/manufacturers/${id}/shippers`, { name, shipperId });
        },
        async removeShipper(id: string, shipperId: string): Promise<void> {
            await http.delete(`/api/v0/admin/manufacturers/${id}/shippers/${shipperId}`);
        },
    },
    accounts: {
        async list(search = ""): Promise<AdminAccountSummary[]> {
            const { data } = await http.get<AdminAccountSummary[]>("/api/v0/admin/accounts", { params: search ? { search } : {} });
            return data;
        },
        async create(name: string, domain: string): Promise<{ id: string }> {
            const { data } = await http.post<{ id: string }>("/api/v0/admin/accounts", { name, domain });
            return data;
        },
        async get(id: string): Promise<AdminAccount> {
            const { data } = await http.get<AdminAccount>(`/api/v0/admin/accounts/${id}`);
            return data;
        },
        async update(id: string, name: string, domain: string): Promise<void> {
            await http.put(`/api/v0/admin/accounts/${id}`, { name, domain });
        },
        async createUser(id: string, email: string, level: AccessLevel): Promise<CreatedUser> {
            const { data } = await http.post<CreatedUser>(`/api/v0/admin/accounts/${id}/users`, { email, level });
            return data;
        },
        async setUserAccess(id: string, userId: string, level: AccessLevel): Promise<void> {
            await http.put(`/api/v0/admin/accounts/${id}/users/${userId}`, { level });
        },
        async removeUserAccess(id: string, userId: string): Promise<void> {
            await http.delete(`/api/v0/admin/accounts/${id}/users/${userId}`);
        },
    },
    carriers: {
        async list(): Promise<Carrier[]> {
            const { data } = await http.get<Carrier[]>("/api/v0/admin/carriers");
            return data;
        },
        async create(name: string, trackingOrderUrl: string): Promise<void> {
            await http.post("/api/v0/admin/carriers", { name, trackingOrderUrl });
        },
        async update(id: string, name: string, trackingOrderUrl: string): Promise<void> {
            await http.put(`/api/v0/admin/carriers/${id}`, { name, trackingOrderUrl });
        },
        async remove(id: string): Promise<void> {
            await http.delete(`/api/v0/admin/carriers/${id}`);
        },
    },
};
